import React from 'react';
import PropTypes from 'prop-types';
import withData from '@bd/bdcore-data';
import { get, orderBy } from 'lodash';
import { css } from '@bd/bdui-styles/emotion';
import { Link } from '@bd/bdcore-named-routes';
import { SanitizeHtml } from '@bd/bdcore-utilities';
import PageHelmet from '@bd/bdcore-pagehelmet';
import PageWrapper from '../../components/PageWrapper';
import PlanDetailHeader from './PlanDetailHeader';
import { colors, typography } from '../../styles';
import HomeCard from '../../components/HomeCard';
import LightBoxPhotoList from '../../components/LightBoxPhotoList';

const styles = css`
  .DetailDescription_wrapper {
    font-family: ${typography.Serif};
    background-size: cover;
    background-position: center center;
    background-image: url('/images/comm-detail-description-bg-fpo.jpg');
    padding: 30px;
    position: relative;
    @media (min-width: 992px) {
      padding: 90px 60px;
    }
    &:before {
      content: '';
      background-color: rgba(63,63,63, .85);
      position: absolute;
      display: block;
      left: 0;
      top: 0;
      right: 0;
      bottom: 0;
      z-index: 1;
      @media (min-width: 992px) {
        left: 62.5%;
      }
    }
    &:after {
      content: '';
      background-color: rgba(53,53,53, .9);
      position: absolute;
      display: none;
      left: 0;
      top: 0;
      right: 0;
      bottom: 0;
      z-index: 2;
      @media (min-width: 992px) {
        display: block;
        right: 37.5%;
      }
    }
  }
  .DetailDescription_inner {
    z-index: 3;
    position: relative;
  }
  .DetailDescription_preTitle {
    font-weight: 200;
    font-size: 40px;
    color: #FFFFFF;
    @media (min-width: 992px) {
      font-size: 80px;
    }
  }
  .DetailDescription_title {
    font-weight: 800;
    font-size: 30px;
    line-height: 40px;
    color: ${colors.color3Darker};
    text-transform: uppercase;
    position: relative;
    margin: 0 0 60px 0;
    &:after {
      width: 125px;
      height: 3px;
      position: absolute;
      display: block;
      content: '';
      bottom: -30px;
      background-color: ${colors.color2};
    }
  }
  .DetailDescription_body {
    font-family: ${typography.SanSerif};
    font-weight: 200;
    font-size: 18px;
    color: #FFFFFF;
    line-height: 37px;
    ol {
       list-style: none;
       counter-reset: item;
     }
     li {
       counter-increment: item;
       margin-bottom: 5px;
     }
     li:before {
       margin-right: 10px;
       content: counter(item);
       background: ${colors.color2};
       border-radius: 100%;
       color: #FFFFFF;
       width: 24px;
       height: 24px;
       line-height: 23px;
       border-radius: 50%:
       text-align: center;
       display: inline-block;
     }
  }
  .DetailDescription_list {
    h3 {
      font-weight: 800;
      font-size: 20px;
      color: #FFFFFF;
      text-transform: uppercase;
      position: relative;
      margin: 60px 0 45px 0;
      @media (min-width: 992px) {
        margin: 0 0 60px 0;
      }
      &:after {
        content: '';
        position: absolute;
        width: 90px;
        height: 3px;
        bottom: -30px;
        left: 0;
        display: block;
        background-color: #3F3F3F;
      }
    }
    ul {
      list-style: none;
      padding: 0;
      margin: 0;
      li {
        a {
          font-weight: 800;
          font-size: 12px;
          line-height: 3;
          color: #FFFFFF;
          text-transform: uppercase;
          @media (min-width: 992px) {
            font-size: 15px;
          }
          span {
            font-size: 11px;
            font-weight: 400;
            margin: 0 0 0 5px;
            color: #E0CF9B;
            @media (min-width: 992px) {
              font-size: 13px;
            }
          }
        }
      }
    }
  }
  .ZinckVirtualTour_wrapper {
    display: inline-block;
    width: 100%;
    max-width: 1200px;
    overflow: hidden;
    position: relative;
    overflow-y: hidden;
    &:before {
      padding-top: 56.25%;
      content: '';
      display: block;
      position: relative;
    }
    iframe {
      position: absolute;
      left: 0;
      right: 0;
      top: -123px;
      width: 100%;
      height: calc(100% + 123px);
      overflow: hidden;
    }
  }
  .DetailSection {
    padding: 45px 0;
    @media (min-width: 992px) {
      padding: 90px 0;
    }
    img {
      max-width: 100%;
    }
    .DetailSection_heading {
      text-transform: uppercase;
      font-family: ${typography.Serif};
      font-size: 24px;
      font-weight: 300;
      color: ${colors.color5};
      position: relative;
      text-align: center;
      margin: 0 0 60px 0;
      @media (min-width: 992px) {
        margin: 0 0 70px 0;
        font-size: 36px;
      }
      span {
        font-weight: 900;
        color: ${colors.color5Darker};
      }
      &:after {
        content: '';
        width: 180px;
        height: 5px;
        bottom: -30px;
        background-color: ${colors.color2};
        left: 0;
        right: 0;
        margin: 0 auto;
        position: absolute;
        display: block;
      }
    }
  }
  .lighter {
    font-weight: 400;
  }
`;

function PlanDetail({ communities, homes, plan }) {
  return (
    <PageWrapper className={styles}>
      <PageHelmet plan={plan} />
      <PlanDetailHeader homes={homes} plan={plan} />
      <div className="DetailDescription">
        <div className="DetailDescription_wrapper">
          <div className="DetailDescription_inner row">
            <div className="DetailDescription_content col-lg-7">
              <h4 className="DetailDescription_title">
                <span className="DetailDescription_preTitle">Plan</span><br />
                Description
              </h4>
              <div className="DetailDescription_body" dangerouslySetInnerHTML={SanitizeHtml(get(plan, 'description'))} />
            </div>
            <div className="DetailDescription_list col-lg-4 ml-auto">
              <h3>Build the {get(plan, 'name')} in the Following Communities</h3>
              <ul>
                {
                  communities.map(community => (
                    <li key={community._id}>
                      <Link
                        name="community-detail"
                        community={community}
                      >
                        {get(community, 'name')}
                        <span>{get(community, 'area')}</span>
                      </Link>
                    </li>
                  ))
                }
              </ul>
            </div>
          </div>
        </div>
      </div>
      {
        get(plan, 'tourUrl') &&
        <div className="DetailSection text-center" id="tour">
          <h3 className="DetailSection_heading"><span>Virtual</span> Tour</h3>
          <div className="ZinckVirtualTour_wrapper">
            <iframe
              src={get(plan, 'tourUrl')}
              frameBorder="0"
              title="Virtual Tour"
              scrolling="no"
            />
          </div>
        </div>
      }
      {
        get(plan, 'elevationPhotos', []).length > 0 &&
        <div className="DetailSection text-center" id="elevations">
          <h3 className="DetailSection_heading"><span>{get(plan, 'name')}</span> Elevations</h3>
          <div className="container">
            <LightBoxPhotoList
              show={8}
              photos={get(plan, 'elevationPhotos', [])}
            />
          </div>
        </div>
      }
      {
        get(plan, 'floorplanPhotos', []).length > 0 &&
        <div className="DetailSection text-center" id="plans">
          <h3 className="DetailSection_heading"><span>Floor</span> Plans</h3>
          <div className="container">
            <LightBoxPhotoList
              show={8}
              photos={get(plan, 'floorplanPhotos', [])}
            />
          </div>
        </div>
      }
      {
        homes.length > 0 &&
        <div className="DetailSection text-center" id="homes">
          <h3 className="DetailSection_heading"><span>{get(plan, 'name')}</span> Move-In Homes</h3>
          <div className="container">
            <div className="row py-4 justify-content-center">
              {
                orderBy(homes, 'sqft', 'desc').map(otherHome => (
                  <div key={otherHome._id} className="col-lg-6 col-xl-4">
                    <HomeCard home={otherHome} />
                  </div>
                ))
              }
            </div>
          </div>
        </div>
      }
      {
        get(plan, 'photos', []).length > 0 &&
        <div className="DetailSection text-center" id="gallery">
          <h3 className="DetailSection_heading"><span>Photo</span> Gallery</h3>
          <div className="container">
            <LightBoxPhotoList
              show={8}
              photos={get(plan, 'photos', [])}
            />
          </div>
        </div>
      }
    </PageWrapper>
  );
}

PlanDetail.propTypes = {
  plan: PropTypes.object.isRequired,
  communities: PropTypes.arrayOf(PropTypes.object).isRequired,
  homes: PropTypes.arrayOf(PropTypes.object),
};

PlanDetail.defaultProps = {
  homes: [],
};

export default withData(
  {
    plan: true,
    communities: true,
    homes: true,
  },
  {
    required: {
      plan: true,
    },
    ifMissing404: true,
  },
)(PlanDetail);



// WEBPACK FOOTER //
// ./src/pages/PlanDetail/index.js